import React, { useEffect, useState } from 'react';
import { Button } from '@headlessui/react';
import { 
  FaPlus, FaFileAlt, FaTrash, FaEye, FaCopy, FaDownload, FaQuestionCircle, FaEdit
} from 'react-icons/fa';
import { 
  authenticatedFetch,
  getBaseUrl
} from '../utils/tokenUtils';
import DocumentForm from './DocumentForm';
import { generateLegalDocument } from '../utils/generateLegalDocument';
import UpgradeModal from './UpgradeModal';
import VideoModal from './VideoModal';
import EditDocumentModal from './EditDocumentModal';

const DOCUMENTS_PER_PAGE = 10;

const DocumentsSection = ({ 
  user, 
  documents, 
  projects, 
  onStatsUpdate, 
  onShowToast,
  onRefreshDocuments 
}) => {
  const [showDocumentForm, setShowDocumentForm] = useState(false);
  const [isSubmittingDocument, setIsSubmittingDocument] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [upgradeModalSection, setUpgradeModalSection] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPublicationId, setEditingPublicationId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil((documents?.length || 0) / DOCUMENTS_PER_PAGE));
  const currentDocuments = (documents || []).slice((currentPage - 1) * DOCUMENTS_PER_PAGE, currentPage * DOCUMENTS_PER_PAGE);
  const visiblePages = Array.from({ length: totalPages }, (_, index) => index + 1).filter(page => {
    return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
  });

  useEffect(() => {
    setCurrentPage(page => Math.min(page, totalPages));
  }, [totalPages]);

  // Check if user can create more documents
  const canCreateDocument = () => {
    const userPlan = user?.subscription?.plan || 'free';
    const currentCount = documents.length;
    const userLimit = user?.subscription?.maxDocuments;
    
    // Enterprise planında sınırsız
    if (userPlan === 'enterprise' || userLimit === -1) {
      return true;
    }
    
    // Kullanıcının limitini kullan, yoksa fallback
    if (userLimit) {
      return currentCount < userLimit;
    }
    
    // Fallback limitler
    switch (userPlan) {
      case 'free':
        return currentCount < 5;
      case 'premium':
        return currentCount < 20;
      default:
        return currentCount < 5;
    }
  };

  // Handle upgrade click
  const handleUpgradeClick = (section = null) => {
    setUpgradeModalSection(section);
    setShowUpgradeModal(true);
  };

  const handleUpgradeSuccess = () => {
    setShowUpgradeModal(false);
    onShowToast('Subscription upgraded successfully! You can now create more documents.', 'success');
    if (onStatsUpdate) {
      onStatsUpdate();
    }
  };

  const resetDocumentForm = () => {
    console.log('Resetting document form');
  };

  const handleCreateDocument = async (documentData) => {
    setIsSubmittingDocument(true);
    try {
      const result = await generateLegalDocument(documentData, user);
      onShowToast('Document generated successfully!', 'success');
      return result;
    } catch (error) {
      console.error('Create document error:', error);
      if (error.message !== 'Authentication failed') {
        onShowToast(`Error occurred while generating document: ${error.message}`, 'error');
      }
      throw error;
    } finally {
      setIsSubmittingDocument(false);
    }
  };

  const handleUpdateDocument = async (documentId, documentData) => {
    setIsSubmittingDocument(true);
    try {
      const response = await authenticatedFetch(`/api/dashboard/user/documents/${documentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(documentData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update document');
      }

      const result = await response.json();
      setShowDocumentForm(false);
      if (onStatsUpdate) {
        onStatsUpdate();
      }
      onShowToast('Document updated successfully!', 'success');
    } catch (error) {
      console.error('Update document error:', error);
      if (error.message !== 'Authentication failed') {
        onShowToast(`Error occurred while updating document: ${error.message}`, 'error');
      }
    } finally {
      setIsSubmittingDocument(false);
    }
  };


  const handleDeleteDocument = async (document) => {
    if (!window.confirm('Are you sure you want to delete this document? This action cannot be undone.')) {
      return;
    }

    try {
      const publicationId = document.metadata?.publicationId;
      let response;
      
      if (publicationId) {
        response = await authenticatedFetch(`/api/privacy-policy/${publicationId}`, {
          method: 'DELETE'
        });
      } else {
        response = await authenticatedFetch(`/api/dashboard/user/documents/${document._id}`, {
          method: 'DELETE'
        });
      }

      if (response.ok) {
        onShowToast('Document deleted successfully', 'success');
        if (onRefreshDocuments) {
          await onRefreshDocuments();
        }
      } else {
        const errorData = await response.json();
        onShowToast(errorData.message || 'Failed to delete document', 'error');
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      onShowToast('Failed to delete document', 'error');
    }
  };

  return (
    <div className="bg-black rounded-2xl border border-gray-800 p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-medium text-white">Documents</h3>
        <div className="flex items-center space-x-3">
          <Button
            onClick={() => setShowVideoModal(true)}
            className="group flex items-center px-4 py-3 rounded-xl transition-all duration-200 bg-gray-800 text-white hover:bg-gray-700 border border-gray-700 data-hover:bg-gray-700 data-focus:ring-white"
          >
            <FaQuestionCircle className="mr-2 h-4 w-4 transition-transform duration-200 group-hover:scale-110" />
            How to Create?
          </Button>
          <Button
            onClick={() => {
              if (!canCreateDocument()) {
                handleUpgradeClick('documents');
                return;
              }
              console.log('UserDashboard: Creating new document');
              resetDocumentForm();
              setShowDocumentForm(true);
            }}
            className={`group flex items-center px-6 py-3 rounded-xl transition-all duration-200 data-hover:bg-gray-700 data-focus:ring-white border ${
              canCreateDocument() 
                ? 'bg-gray-800 text-white hover:bg-gray-700 border-gray-700' 
                : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700 border-indigo-500 animate-pulse'
            }`}
          >
            <FaPlus className={`mr-2 h-4 w-4 transition-transform duration-200 ${
              canCreateDocument() ? 'group-hover:rotate-90' : 'animate-bounce'
            }`} />
            {canCreateDocument() ? 'Create Document' : 'Upgrade to Create More'}
          </Button>
        </div>
      </div>

      {(!documents || documents.length === 0) ? (
        <div className="text-center py-12">
          <FaFileAlt className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No Documents</h3>
          <p className="text-gray-400 mb-6">Create your first document to get started.</p>
          <div className="flex items-center justify-center space-x-3">
            <Button
              onClick={() => setShowVideoModal(true)}
              className="group flex items-center px-4 py-3 rounded-xl transition-all duration-200 bg-gray-800 text-white hover:bg-gray-700 border border-gray-700 data-hover:bg-gray-700 data-focus:ring-white"
            >
              <FaQuestionCircle className="mr-2 h-4 w-4 transition-transform duration-200 group-hover:scale-110" />
              How to Create?
            </Button>
            <Button
              onClick={() => {
                if (!canCreateDocument()) {
                  handleUpgradeClick('documents');
                  return;
                }
                console.log('Creating new document from empty state');
                resetDocumentForm();
                setShowDocumentForm(true);
              }}
              className={`group flex items-center px-6 py-3 rounded-xl transition-all duration-200 data-hover:bg-gray-700 data-focus:ring-white border ${
                canCreateDocument() 
                  ? 'bg-gray-800 text-white hover:bg-gray-700 border-gray-700' 
                  : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700 border-indigo-500 animate-pulse'
              }`}
            >
              <FaPlus className={`mr-2 h-4 w-4 transition-transform duration-200 ${
                canCreateDocument() ? 'group-hover:rotate-90' : 'animate-bounce'
              }`} />
              {canCreateDocument() ? 'Create Document' : 'Upgrade to Create More'}
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {currentDocuments.map((document) => (
            <div key={document._id} className="bg-black rounded-lg border border-gray-800 hover:border-gray-700 transition-all duration-200">
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center flex-1">
                    <div className="h-10 w-10 bg-gray-800 rounded-lg flex items-center justify-center mr-4">
                      <FaFileAlt className="h-5 w-5 text-gray-300" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-4">
                        <h4 className="text-sm font-medium text-white">{document.title}</h4>
                      </div>
                      <div className="flex items-center space-x-6 mt-2 text-xs text-gray-400">
                        <span>Created: {new Date(document.createdAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex space-x-2 ml-4">
                    <button
                      onClick={() => {
                        const publicationId = document.metadata?.publicationId;
                        if (publicationId) {
                          window.open(`${getBaseUrl()}/api/publication/${publicationId}`, '_blank');
                        } else if (document._id) {
                          window.open(`${getBaseUrl()}/api/dashboard/user/documents/${document._id}/view`, '_blank');
                        } else {
                          onShowToast('Document not available', 'error');
                        }
                      }}
                      className="flex items-center px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors border border-gray-700"
                    >
                      <FaEye className="mr-2" />
                      View Online
                    </button>
                    <button
                      onClick={() => {
                        const pubId = document.metadata?.publicationId;
                        if (pubId) {
                          setEditingPublicationId(pubId);
                          setShowEditModal(true);
                        } else {
                          onShowToast('This document cannot be edited. Only generated documents with publication ID can be edited.', 'error');
                        }
                      }}
                      disabled={!document.metadata?.publicationId}
                      className="flex items-center px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors border border-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FaEdit className="mr-2" />
                      Edit
                    </button>
                    <button
                      onClick={async () => {
                        const publicationId = document.metadata?.publicationId;
                        if (publicationId) {
                          try {
                            const url = `${getBaseUrl()}/api/publication/${publicationId}`;
                            await navigator.clipboard.writeText(url);
                            onShowToast('Link copied to clipboard!', 'success');
                          } catch (error) {
                            onShowToast('Failed to copy link', 'error');
                          }
                        } else if (document._id) {
                          try {
                            const response = await authenticatedFetch(`/api/dashboard/user/documents/${document._id}/link`);
                            if (response.ok) {
                              const data = await response.json();
                              await navigator.clipboard.writeText(data.link);
                              onShowToast('Link copied to clipboard!', 'success');
                            } else {
                              onShowToast('Failed to get document link', 'error');
                            }
                          } catch (error) {
                            onShowToast('Failed to copy link', 'error');
                          }
                        } else {
                          onShowToast('Document not available', 'error');
                        }
                      }}
                      className="flex items-center px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors border border-gray-700"
                    >
                      <FaCopy className="mr-2" />
                      Copy Link
                    </button>
                    <button
                      onClick={() => {
                        const publicationId = document.metadata?.publicationId;
                        if (publicationId) {
                          window.open(`${getBaseUrl()}/api/privacy-policy/download/${publicationId}`, '_blank');
                        } else if (document._id) {
                          window.open(`${getBaseUrl()}/api/dashboard/user/documents/${document._id}/download`, '_blank');
                        } else {
                          onShowToast('Document not available', 'error');
                        }
                      }}
                      className="flex items-center px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors border border-gray-700"
                    >
                      <FaDownload className="mr-2" />
                      Download PDF
                    </button>
                    <button
                      onClick={() => handleDeleteDocument(document)}
                      className="flex items-center px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-red-600 transition-colors border border-gray-700"
                    >
                      <FaTrash className="mr-2" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
          {totalPages > 1 && (
            <div className="flex justify-center pt-2">
              <div className="flex items-center gap-2 overflow-x-auto">
                <button
                  type="button"
                  onClick={() => setCurrentPage(page => Math.max(1, page - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 bg-gray-800 text-gray-200 text-sm rounded-lg border border-gray-700 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                {visiblePages.map((page, index) => {
                  const previousPage = visiblePages[index - 1];
                  return (
                    <React.Fragment key={page}>
                      {previousPage && page - previousPage > 1 && <span className="px-1 text-gray-500">...</span>}
                      <button
                        type="button"
                        onClick={() => setCurrentPage(page)}
                        className={`min-w-10 px-3 py-2 text-sm rounded-lg border transition-colors ${
                          currentPage === page
                            ? 'bg-indigo-600 border-indigo-500 text-white'
                            : 'bg-gray-800 border-gray-700 text-gray-200 hover:bg-gray-700'
                        }`}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  );
                })}
                <button
                  type="button"
                  onClick={() => setCurrentPage(page => Math.min(totalPages, page + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 bg-gray-800 text-gray-200 text-sm rounded-lg border border-gray-700 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Document Form Modal */}
      <DocumentForm
        isOpen={showDocumentForm}
        onClose={() => {
          setShowDocumentForm(false);
        }}
        onSubmit={handleCreateDocument}
        onSuccess={() => {
          if (onRefreshDocuments) {
            onRefreshDocuments();
          }
          if (onStatsUpdate) {
            onStatsUpdate();
          }
        }}
        document={null}
        projects={projects}
        isLoading={isSubmittingDocument}
        user={user}
      />

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        user={user}
        onUpgrade={handleUpgradeSuccess}
        highlightSection={upgradeModalSection}
      />

      {/* Video Modal */}
      <VideoModal
        isOpen={showVideoModal}
        onClose={() => setShowVideoModal(false)}
        videoUrl="https://www.youtube.com/embed/oedoaW9z2-k"
        title="How to Create a Document?"
      />

      {/* Edit Document Modal */}
      <EditDocumentModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingPublicationId(null);
        }}
        publicationId={editingPublicationId}
        onSuccess={() => {
          onShowToast('Document updated successfully!', 'success');
          if (onRefreshDocuments) {
            onRefreshDocuments();
          }
        }}
      />
    </div>
  );
};

export default DocumentsSection;
